import React, { useEffect, useState } from 'react'
import { Flex, Grid, Text } from '@adsk/alloy-react-box'
import FormField from '@adsk/alloy-react-form-field'
import Theme from '@adsk/alloy-react-theme'
import {
	OnboardingUserAppContextType,
	useOnboardingUserContext,
} from '../../store'
import { toggleReadOnlyMode } from '../../store/actions'
import Button, { LinkButton } from '@adsk/alloy-react-button'
import styled from 'styled-components'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { getPackageInfo } from '../../utils'
import Tag from '@adsk/alloy-react-tag'
import { NAPIAvatar, Avatar, maxWidthDevice } from '@adsk/napi-shared'
import { SELECTION_LIST_KEYS, STATIC_IMAGES } from '../../constants'
import _ from 'lodash'
import { getInitials } from '../../utils/helpers'
import { useTranslation } from '../../../i18n'
import { AlertWarningIcon } from '@adsk/alloy-react-icon'
const ResponsiveGrid = styled(Grid)`
	@media${maxWidthDevice.mobileS} {
		margin-top: 5%;
	}
`

const PaddedFlex = styled(Flex)`
	padding: 1px 0;
	margin: 2px 0;
`
const CompanyTypeContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
`

const ReadOnlyProfileComponent: React.FC = (): JSX.Element => {
	const { name, version } = getPackageInfo()
	const { typography, colors } = Theme
	const {
		dispatch,
		state: { userInfo, selectionsList },
	} = useOnboardingUserContext() as OnboardingUserAppContextType
	const { t } = useTranslation()
	const [jobTitleList, setJobTitleList] = useState<Array<string>>([])
	const [companyTypeList, setCompanyTypeList] = useState<Array<string>>([])
	useEffect(() => {
		if (!_.isEmpty(selectionsList)) {
			selectionsList?.forEach((constantList) => {
				if (constantList.name === SELECTION_LIST_KEYS.JOB_TITLES) {
					let list = _.map(constantList.optionsList, 'value')
					setJobTitleList(list)
				}
				if (constantList.name === SELECTION_LIST_KEYS.COMPANY_TYPES) {
					let list = _.map(constantList.optionsList, 'value')
					setCompanyTypeList(list)
				}
			})
		}
	}, [selectionsList])

	const prePopulatedCompanyName = _.get(
		userInfo,
		'companyName'
	) as unknown as string

	const prePopulatedCompanyType = _.get(userInfo, 'companyType', [])
	const getSelectedCompanyTypes = (): Array<string> => {
		let results: Array<string> = []
		if (companyTypeList.length > 0) {
			results = prePopulatedCompanyType.filter((x) =>
				companyTypeList.includes(x)
			)
			if (
				_.difference(prePopulatedCompanyType, companyTypeList).length > 0 &&
				!results.includes('Other')
			) {
				results.push('Other')
			}
		}
		return results
	}

	const CompanyTypeFetchFailure = () => {
		return (
			<>
				<AlertWarningIcon size={14} />
				<Text
					style={{
						...typography.bodySmall,
						color: colors.charcoal700,
						paddingLeft: 10,
					}}
				>
					{t('COMPANY_TYPE_NOT_AVAILABLE')}
				</Text>
			</>
		)
	}

	return (
		<React.Fragment>
			<Grid
				gridColumnGap={'2.5%'}
				padding={['5%', 'auto']}
				style={{
					backgroundColor: colors.adskBlue100,
				}}
				gridAutoColumns={'auto'}
				gridTemplateColumns={['auto', '25% auto']}
				data-testid={'profileComponent'}
			>
				<Grid justifyContent={'center'}>
					<div style={{ position: 'relative', height: '100%' }}>
						<NAPIAvatar
							data-testid={'Avatar'}
							styles={{
								alignSelf: 'center',
							}}
							name={getInitials(
								`${userInfo?.firstName?.trim()} ${userInfo?.lastName?.trim()}`
							)}
							size={Avatar.SIZES.XXLARGE_128}
							image={userInfo?.avatarUrl}
							fallbackImage={STATIC_IMAGES.PLACEHOLDER}
							showPlaceHolder={true}
							placeHolder={{
								type: 'ProgressRing',
								size: 'small',
								styles: {
									alignSelf: 'center',
								},
							}}
						/>
					</div>
				</Grid>
				<ResponsiveGrid justifyContent={['center', 'flex-start']}>
					<Flex
						flexDirection={'column'}
						justifyContent={'center'}
						alignItems={['center', 'flex-start']}
					>
						{(userInfo?.firstName || userInfo?.lastName) && (
							<PaddedFlex flexDirection={['column', 'row']}>
								<Text
									style={{
										...typography.bodyMediumBold,
										wordBreak: 'break-all',
									}}
									data-testid="readOnlyNameId"
								>
									{userInfo?.firstName} {userInfo?.lastName}
								</Text>
							</PaddedFlex>
						)}
						{userInfo?.phone && (
							<PaddedFlex flexDirection={['column', 'row']}>
								<Text
									data-testid="readOnlyPhoneId"
									style={typography.bodyMedium}
								>
									{userInfo?.phone}
								</Text>
							</PaddedFlex>
						)}
						<PaddedFlex flexDirection={['column', 'row']}>
							<Text style={typography.bodyMedium}>
								<LinkButton
									role={'link'}
									onClick={() => {
										track(
											ANALYTICS_TYPES.NAPI_BUILDERS_PROFILE_EDIT_PROFILE_CLICKED,
											{
												package: name,
												component: ReadOnlyProfileComponent.name,
												version,
												profileMode: 'view',
												action: 'mailto_link_clicked',
											}
										)
										window.location.href = `mailto:${userInfo?.email}`
									}}
									size={LinkButton.SIZES.SMALL}
								>
									{userInfo?.email}
								</LinkButton>
							</Text>
						</PaddedFlex>
						{userInfo?.title && (
							<PaddedFlex flexDirection={['column', 'row']}>
								<Text
									data-testid={'readOnlyJobTitleId'}
									style={{
										...typography.bodyMedium,
										color: colors.charcoal900,
										wordBreak: 'break-word',
									}}
								>
									{userInfo.title}
								</Text>
							</PaddedFlex>
						)}
					</Flex>
				</ResponsiveGrid>
			</Grid>
			<Grid paddingTop={'2%'}>
				<Flex justifyContent={'flex-end'} alignItems={'flex-end'}>
					<Button
						data-testid={'toggleEditButtonTestId'}
						variant={Button.VARIANTS.SECONDARY}
						size={Button.SIZES.SMALL}
						onClick={() => {
							track(
								ANALYTICS_TYPES.NAPI_BUILDERS_PROFILE_EDIT_PROFILE_CLICKED,
								{
									package: name,
									component: ReadOnlyProfileComponent.name,
									version,
									profileMode: 'edit',
									action: 'edit_profile_clicked',
								}
							)
							dispatch(toggleReadOnlyMode())
						}}
					>
						{t('EDIT_PROFILE_BUTTON')}
					</Button>
				</Flex>
			</Grid>
			<Grid gridGap={2} marginBottom={'10%'}>
				{prePopulatedCompanyName && (
					<FormField
						label={t('COMPANY_NAME')}
						labelVariant={FormField.LABEL_VARIANTS.TOP}
						style={{ marginBottom: '2%' }}
					>
						<Text
							style={typography.bodyMedium}
							data-testid={'readOnlyCompanyNameTestId'}
						>
							{prePopulatedCompanyName}
						</Text>
					</FormField>
				)}
				{prePopulatedCompanyType && prePopulatedCompanyType.length > 0 && (
					<FormField
						label={t('COMPANY_TYPE')}
						labelVariant={FormField.LABEL_VARIANTS.TOP}
						style={typography.bodySmall}
					>
						<CompanyTypeContainer>
							{companyTypeList && companyTypeList.length > 0 ? (
								getSelectedCompanyTypes().map((type, i) => (
									<Tag
										key={i}
										style={{
											margin: 2,
											pointerEvents: 'none',
											backgroundColor: colors.adskBlue100,
										}}
										selected
									>
										{type}
									</Tag>
								))
							) : (
								<CompanyTypeFetchFailure />
							)}
						</CompanyTypeContainer>
					</FormField>
				)}
			</Grid>
		</React.Fragment>
	)
}

export default ReadOnlyProfileComponent
